import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, MapPin, RefreshCw, Building2 } from "lucide-react";

const LEVELS = [
  { key: "locality", label: "Localité",  hint: "Commune / village / quartier" },
  { key: "cercle",   label: "Cercle",    hint: "Administration territoriale" },
  { key: "region",   label: "Région",    hint: "Région / district" },
];

/**
 * AreaStatsTab — Iter50 administrative-area rollups. Joins each alert
 * against `geocode_cache` (populated by the Nominatim backfill) and
 * lets the operator switch between three granularities: locality →
 * cercle → region. Top-25 bar chart by count, with a coverage badge
 * that surfaces what % of alerts were resolved to a real locality vs
 * the offline bbox fallback.
 */
export const AreaStatsTab = () => {
  const [level, setLevel] = useState("locality");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (lvl) => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/alerts/area-stats", {
        params: { level: lvl, limit: 25 },
      });
      setData(r.data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(level); }, [level, load]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12 text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const items = data?.items || [];
  const max = Math.max(1, ...items.map((i) => i.count));
  const coverage = Math.round((data?.geocode_coverage || 0) * 100);

  return (
    <div className="flex-1 px-3 sm:px-5 py-4 pb-8 space-y-4" data-testid="admin-area-stats-tab">
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
        <MapPin className="w-4 h-4 text-mali-yellow" strokeWidth={2.4} />
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-300">
          Répartition administrative
        </span>
        <span className="ml-auto font-mono text-[10px] text-zinc-500 tabular-nums">
          {data?.total_alerts || 0} alertes · {data?.unique_areas || 0} zones · {coverage}% géocodées
        </span>
      </div>

      <div className="grid grid-cols-3 gap-1.5" data-testid="area-level-switcher">
        {LEVELS.map(({ key, label, hint }) => (
          <button
            key={key}
            type="button"
            data-testid={`area-level-${key}`}
            onClick={() => setLevel(key)}
            className={`p-2 border text-left transition-colors ${
              level === key
                ? "border-mali-yellow bg-mali-yellow/10 text-mali-yellow"
                : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
            }`}
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.18em]">{label}</div>
            <div className="text-[9px] text-zinc-500 mt-0.5">{hint}</div>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
          Top {items.length} {level === "locality" ? "localités" : level === "cercle" ? "cercles" : "régions"}
        </div>
        <button
          type="button"
          onClick={() => load(level)}
          disabled={refreshing}
          data-testid="area-refresh-btn"
          className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 hover:text-zinc-100 flex items-center gap-1 disabled:opacity-60"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Mise à jour" : "Actualiser"}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-zinc-800 bg-[#0f1318] py-12 text-center text-zinc-500 text-xs">
          Aucune zone à afficher pour ce niveau.
        </div>
      ) : (
        <div className="border border-zinc-800 bg-[#0f1318]" data-testid="area-rows">
          {items.map((row, idx) => {
            const topType = Object.entries(row.by_type || {}).sort((a, b) => b[1] - a[1])[0];
            const pct = Math.round((row.count / max) * 100);
            return (
              <div
                key={`${row.area}-${idx}`}
                data-testid={`area-row-${idx}`}
                className="px-3 py-2.5 border-b border-zinc-800/80 last:border-b-0 flex items-center gap-3"
              >
                <span className="font-mono text-[10px] text-zinc-600 w-5 tabular-nums">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-3 h-3 text-mali-yellow shrink-0" />
                    <span className="text-xs font-bold text-zinc-100 truncate" title={row.area}>{row.area}</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-zinc-800/80 relative overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-mali-green to-mali-yellow"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {topType && (
                    <div className="mt-1 text-[9px] text-zinc-500 font-mono uppercase tracking-[0.15em]">
                      Principal : {topType[0]} ({topType[1]})
                    </div>
                  )}
                </div>
                <span className="font-mono text-sm font-bold tabular-nums text-zinc-200 shrink-0 w-12 text-right">
                  {row.count}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-[10px] text-zinc-600 text-center font-mono">
        Source : geocode_cache (Nominatim) · niveau « {level} »
      </div>
    </div>
  );
};
